import { jsPDF } from 'jspdf';
import { formatInvoiceMoney } from './buildOrderInvoiceData';

/** @typedef {import('./buildOrderInvoiceData').OrderInvoiceData} OrderInvoiceData */
/** @typedef {import('../constants/invoiceSeller').InvoiceSeller} InvoiceSeller */

const PAGE_MARGIN = 40;
const CONTENT_WIDTH = 515;

/**
 * @param {InvoiceSeller} seller
 * @returns {string[]}
 */
export function buildInvoiceSellerLines(seller) {
  const city = String(seller.city || '').trim();
  const province = String(seller.province || '').trim();
  const cityLine = [seller.postalCode, city].filter(Boolean).join(' ');
  const provinceLine = province && province.toLowerCase() !== city.toLowerCase() ? province : '';

  return [
    seller.legalName,
    seller.taxId ? `NIF/CIF: ${seller.taxId}` : '',
    seller.addressLine,
    cityLine,
    provinceLine,
    seller.country,
    seller.email,
    seller.phone,
  ].filter(Boolean);
}

/**
 * @param {OrderInvoiceData} invoiceData
 * @returns {number}
 */
function getInvoiceFooterHeight(invoiceData) {
  const totalsRows = invoiceData.vatRate > 0 ? 3 : 2;
  const tableEndOffset = 18;
  const totalsBlockHeight = totalsRows * 14;
  const paymentStatusBlockHeight = 10 + 14 + 18;
  const footerNoteHeight = 12;
  return tableEndOffset + totalsBlockHeight + paymentStatusBlockHeight + footerNoteHeight;
}

/**
 * @param {import('jspdf').jsPDF} doc
 * @param {number} startY
 * @param {OrderInvoiceData} invoiceData
 * @returns {number}
 */
function drawLineItemsTable(doc, startY, invoiceData) {
  const columns = [
    { label: 'Descripción', x: PAGE_MARGIN, width: 240 },
    { label: 'Cant.', x: PAGE_MARGIN + 240, width: 40 },
    { label: 'Precio unit.', x: PAGE_MARGIN + 280, width: 90 },
    { label: 'Importe', x: PAGE_MARGIN + 370, width: 90 },
  ];
  const pageHeight = doc.internal.pageSize.height;
  const bottomMargin = PAGE_MARGIN;

  /** @param {number} headerY @returns {number} */
  const drawTableHeader = (headerY) => {
    doc.setFillColor(245, 245, 245);
    doc.rect(PAGE_MARGIN, headerY - 10, CONTENT_WIDTH, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    columns.forEach((column) => {
      doc.text(column.label, column.x, headerY);
    });
    return headerY + 16;
  };

  let y = drawTableHeader(startY);
  const footerHeight = getInvoiceFooterHeight(invoiceData);

  doc.setFont('helvetica', 'normal');
  invoiceData.items.forEach((item, index) => {
    const titleLines = doc.splitTextToSize(item.title, columns[0].width - 8);
    const rowHeight = Math.max(14, titleLines.length * 12);
    const reservedFooterHeight = index === invoiceData.items.length - 1 ? footerHeight : 0;

    if (y + rowHeight + reservedFooterHeight > pageHeight - bottomMargin) {
      doc.addPage();
      y = drawTableHeader(PAGE_MARGIN);
      doc.setFont('helvetica', 'normal');
    }

    doc.text(titleLines, columns[0].x, y);
    doc.text(String(item.qty), columns[1].x, y);
    doc.text(formatInvoiceMoney(item.unitPriceCents, invoiceData.currency), columns[2].x, y);
    doc.text(formatInvoiceMoney(item.lineTotalCents, invoiceData.currency), columns[3].x, y);
    y += rowHeight;
  });

  doc.setDrawColor(220, 220, 220);
  doc.line(PAGE_MARGIN, y + 4, PAGE_MARGIN + CONTENT_WIDTH, y + 4);
  return y + 18;
}

/**
 * @param {import('jspdf').jsPDF} doc
 * @param {number} startY
 * @param {OrderInvoiceData} invoiceData
 * @returns {number}
 */
function drawTotals(doc, startY, invoiceData) {
  let y = startY;
  const labelX = PAGE_MARGIN + 300;
  const valueX = PAGE_MARGIN + 430;

  const rows = [
    ['Base imponible', formatInvoiceMoney(invoiceData.subtotalCents, invoiceData.currency)],
  ];

  if (invoiceData.vatRate > 0) {
    rows.push([
      `IVA (${invoiceData.vatRate}%)`,
      formatInvoiceMoney(invoiceData.vatAmountCents, invoiceData.currency),
    ]);
  }

  rows.push(['Total', formatInvoiceMoney(invoiceData.totalCents, invoiceData.currency)]);

  doc.setFontSize(10);
  rows.forEach(([label, value], index) => {
    const isTotal = index === rows.length - 1;
    doc.setFont('helvetica', isTotal ? 'bold' : 'normal');
    doc.text(label, labelX, y);
    doc.text(value, valueX, y, { align: 'right' });
    y += 14;
  });

  return y;
}

/**
 * @param {OrderInvoiceData} invoiceData
 * @returns {import('jspdf').jsPDF}
 */
export function createOrderInvoicePdf(invoiceData) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  let y = PAGE_MARGIN;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('FACTURA', PAGE_MARGIN, y);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`N.º ${invoiceData.invoiceNumber}`, PAGE_MARGIN + 360, y);
  doc.text(`Fecha: ${invoiceData.issueDate}`, PAGE_MARGIN + 360, y + 14);
  doc.text(`Pedido: #${invoiceData.orderId.slice(-6)}`, PAGE_MARGIN + 360, y + 28);
  y += 48;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Emisor', PAGE_MARGIN, y);
  doc.text('Cliente', PAGE_MARGIN + 280, y);
  y += 16;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const sellerLines = buildInvoiceSellerLines(invoiceData.seller);

  const buyerLines = [
    invoiceData.buyer.name,
    invoiceData.buyer.email,
    invoiceData.buyer.phone,
    invoiceData.buyer.address,
    [invoiceData.buyer.postalCode, invoiceData.buyer.city].filter(Boolean).join(' '),
    invoiceData.buyerCountryLabel,
  ].filter(Boolean);

  const sellerHeight = sellerLines.length * 12;
  const buyerHeight = buyerLines.length * 12;
  sellerLines.forEach((line, index) => {
    doc.text(line, PAGE_MARGIN, y + index * 12);
  });
  buyerLines.forEach((line, index) => {
    doc.text(line, PAGE_MARGIN + 280, y + index * 12);
  });
  y += Math.max(sellerHeight, buyerHeight) + 24;

  y = drawLineItemsTable(doc, y, invoiceData);

  const pageHeight = doc.internal.pageSize.height;
  const totalsFooterHeight = getInvoiceFooterHeight(invoiceData) - 18;
  if (y + totalsFooterHeight > pageHeight - PAGE_MARGIN) {
    doc.addPage();
    y = PAGE_MARGIN;
  }

  y = drawTotals(doc, y, invoiceData);

  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.text(`Estado del pago: ${invoiceData.paymentStatus}`, PAGE_MARGIN, y);
  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(
    'Documento generado automáticamente tras la confirmación del pago.',
    PAGE_MARGIN,
    y,
  );
  doc.setTextColor(0, 0, 0);

  return doc;
}

/**
 * @param {OrderInvoiceData} invoiceData
 */
export function generateOrderInvoicePdf(invoiceData) {
  const doc = createOrderInvoicePdf(invoiceData);
  doc.save(`factura-${invoiceData.invoiceNumber}.pdf`);
}
